import axios from 'axios';

import { setCookie } from '@/utils/cookie';

import { UpdateRestaurantSchemaType } from '@/components/organisms/RestaurantForm/schema';

import { TagResponse } from './category';

export interface ILocation {
  lat: number;
  lng: number;
}

export enum BusinessStatus {
  OPERATIONAL = 'OPERATIONAL',
  CLOSED_TEMPORARILY = 'CLOSED_TEMPORARILY',
  CLOSED_PERMANENTLY = 'CLOSED_PERMANENTLY'
}

export interface IDataPicture {
  url: string;
}

export interface IDataPictures {
  thumbnail?: IDataPicture;
  lowResolution?: IDataPicture;
  standardResolution?: IDataPicture;
  originalResolution?: IDataPicture;
}

export type Picture = {
  id: string;
  principal?: boolean;
  authorName?: string;
  authorLink?: string;
  sizes: IDataPictures;
  alt: string;
  score: number;
};

export type Review = {
  author: string;
  rating: number;
  text: string;
  postedAt: string;
};

export interface IReadableOpeningHours {
  0: string[] | null;
  1: string[] | null;
  2: string[] | null;
  3: string[] | null;
  4: string[] | null;
  5: string[] | null;
  6: string[] | null;
}

export type Menu = {
  name: string;
  description: string | null;
  price?: string;
  category: string;
};

export type DigitalCertificate = {
  id: string;
  restaurantName: string;
  address: string;
  alcohol: boolean;
  date: string;
  firstName: string;
  lastName: string;
  company: string;
  suppliers: string[];
};

export type RestaurantResponse = {
  id: string;
  active: boolean;
  slug: string;
  name: string;
  formattedAddress: string;
  cityName: string | null;
  citySlug: string | null;
  emailOwner?: string;
  emailProposal?: string;
  departmentName: string | null;
  regionName: string | null;
  location: ILocation;
  gmapPlaceId: string;
  averageAwaitTime?: number | null;
  description?: string | null;
  accommodationCapacity?: number | null;
  budget: number | null;
  new?: boolean;
  businessStatus?: BusinessStatus;
  mainPictureUrl: string | null;
  pictures?: Picture[];
  certificate?: Picture; // ici à supprimer après avoir migré vers le nouveau certificat
  digitalCertificate?: DigitalCertificate;
  favorite?: boolean;
  distance?: number;
  tags: TagResponse[];
  phoneNumber?: string;
  website?: string | null;
  socialMedia?: {
    facebook: string | null;
    instagram: string | null;
  };
  delivery?: {
    uberEats: string | null;
    directUberEats: string | null;
    deliveroo: string | null;
    directDeliveroo: string | null;
    justEat: string | null;
    directJustEat: string | null;
    custom: string | null;
    directCustom: string | null;
    customDeliveryLink: string | null;
  };
  services?: {
    takeAway: boolean;
    delivery: boolean;
  };
  rating: number | null;
  ratingCount: number | null;
  reviews?: Review[];
  openingHours?: IReadableOpeningHours;
  simpleOpeningHours?: string;
  UTCOffset?: number;
  gmapUrl?: string;
  featuredReview?: Review;
  weight?: number;
  isCurrentlyOpen?: boolean;
  alcohol?: boolean;
  hasMenu?: boolean;
  addedByOwner?: boolean;
  menu?: Menu[] | null;
  promo: string | null;
  emoji: string | null;
  averageWaitTime?: string;
  city?: {
    location: ILocation;
  };
  isPartner: boolean;
  certified?: boolean;
  validationStatus: ValidationStatus;
  validationStatusHistory: ValidationStatusHistoryInterface[];
  lastNameOwner?: string;
  firstNameOwner?: string;
  phoneNumberOwner?: string;
};

export interface ValidationStatusHistoryInterface {
  status: ValidationStatus;
  date: string;
  comment: string | null;
  authorRole: AuthorRole;
}

export interface UpdateRestaurantOptions {
  uberEatsLink?: string;
  deliverooLink?: string;
  justEatLink?: string;
  customDeliveryLink?: string;
  alcohol: boolean;
  specialties: string;
  pictures: string[];
}

export type RestaurantsResponse = {
  restaurants: RestaurantResponse[];
  place?: PlaceResponse;
  tag?: TagResponse;
  city?: CityResponse;
  location?: ILocation;
};

export type PlaceResponse = {
  placeId: string;
  name: string;
  matchedSubstring: Matching[];
  restaurantId?: string;
  exist: boolean;
  tags: TagResponse[];
};

export type Matching = {
  offset: number;
  length: number;
};

export interface CityResponse {
  id: string;
  slug: string;
  name: string;
  displayName: string;
  location: Location;
  description: string;
}
export type FetchAutocompletePlaceParams = {
  search: string;
  limit?: number;
  lat?: number;
  lng?: number;
};

export type PlacesResponse = {
  places: PlaceResponse[];
};

export type ValidationStatus = 'ACCEPTED' | 'PENDING' | 'REJECTED';

export type AuthorRole = 'support' | 'owner' | 'visitor';

export interface SimpleRestaurantResponse {
  id: string;
  name: string;
  active: boolean;
  validationStatus: ValidationStatus;
  cityName: string | undefined;
}

export interface SimpleRestaurantsResponse {
  restaurants: SimpleRestaurantResponse[];
}

export type Restaurant = {
  restaurant: RestaurantResponse;
};

export type FetchRestaurantOptions = {
  id: string;
  withMenu?: boolean;
};

export type AddRestaurantParams = {
  id?: string;
  placeId?: string;
  autocompleteName?: string;
  email?: string;
  phoneNumberOwner?: string;
  firstName?: string;
  lastName?: string;
  justEatLink?: string | null;
  deliverooLink?: string | null;
  uberEatsLink?: string | null;
  isPlaceAlreadyRegistered?: boolean;
  customDeliveryLink?: string | null;
  alcohol?: boolean;
  tagIds: string[];
  suppliers?: string[];
  company?: string;
};

export type AddRestaurantParamsFromBO = {
  id?: string;
  placeId?: string;
  autocompleteName?: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  justEatLink?: string | null;
  deliverooLink?: string | null;
  uberEatsLink?: string | null;
  isPlaceAlreadyRegistered?: boolean;
  customDeliveryLink?: string | null;
  alcohol?: boolean;
  tags: string[];
  suppliers?: string[];
  company?: string;
};

export enum StatusStripe {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  PAST_DUE = 'past_due',
  TRIALING = 'trialing',
  UNPAID = 'unpaid',
  PENDING_PAYMENT = 'pending_payment'
}
export interface CityStripeStatus {
  cityId: string;
  displayName: string;
  restaurantId?: string;
}

export type AddRestaurantResponse = {
  message: string;
  code: string;
};
export type FetchRestaurantsByOwnerParams = {
  search?: string;
};

export type UpdateRestaurantResponse = {
  message: string;
};

export type DeleteRestaurantResponse = {
  message: string;
};

export type RestaurantPictureParams = {
  restaurantId: string;
  pictureId: string;
};

export type UpdateRestaurantPictureParams = {
  restaurantId: string;
  pictureId: string;
  principal: boolean;
};

export type AddRestaurantPictureParams = {
  restaurantId: string;
  file: File;
};

export type RestaurantCheckTokenResponse = {
  message: string;
  code: string;
  redirectUrl: string;
};

export interface GetRestaurantPictureResponse {
  picture: ISerializedPicture;
}

export interface GetRestaurantPicturesResponse {
  pictures: ISerializedPicture[];
}

export interface ISerializedPicture {
  id: string;
  principal?: boolean;
  authorName?: string;
  authorLink?: string;
  sizes: IDataPictures;
  alt: string;
  score: number;
}

export const getRestaurants = async (): Promise<RestaurantResponse[]> => {
  const { data } = await axios.get(`/protected/restaurants`);
  return data.companies;
};

export const updateRestaurant = async (params: UpdateRestaurantSchemaType): Promise<UpdateRestaurantResponse> => {
  const restaurantId = params.restaurantId;

  const updateRestaurant: { [key: string]: any } = {
    alcohol: params.alcohol
  };

  if (params.tagIds && params.tagIds?.length > 0) {
    updateRestaurant.tagIds = params.tagIds;
  }
  if (params.phoneNumberOwner) {
    updateRestaurant.phoneNumberOwner = params.phoneNumberOwner;
  }
  if (params.uberEatsLink) {
    updateRestaurant.uberEatsLink = params.uberEatsLink;
  }
  if (params.deliverooLink) {
    updateRestaurant.deliverooLink = params.deliverooLink;
  }
  if (params.justEatLink) {
    updateRestaurant.justEatLink = params.justEatLink;
  }
  if (params.customDeliveryLink) {
    updateRestaurant.customDeliveryLink = params.customDeliveryLink;
  }
  if (params.instagramLink) {
    updateRestaurant.instagramLink = params.instagramLink;
  }
  if (params.facebookLink) {
    updateRestaurant.facebookLink = params.facebookLink;
  }

  const response = await axios.put(`/protected/restaurant/${restaurantId}`, updateRestaurant);

  return response.data;
};

export const fetchSponsoredRestaurantsRequest = async (): Promise<RestaurantResponse[]> => {
  const { data } = await axios.get<RestaurantsResponse>(`/restaurant/starred`);
  return data.restaurants;
};

export const fetchLastRestaurantsRequest = async (): Promise<RestaurantResponse[]> => {
  const { data } = await axios.get<RestaurantsResponse>(`/restaurant/last-added`);
  return data.restaurants;
};

// il fait un fetch de tous les restos de mrh
export const fetchAutocompletePlaceRequest = async ({ search, limit, lat, lng }: FetchAutocompletePlaceParams): Promise<PlaceResponse[]> => {
  const params = {
    search,
    limit,
    location: lat && lng ? { lat, lng } : undefined
  };
  const { data } = await axios.get<PlacesResponse>(`/restaurant/gmap/autocomplete`, { params });
  return data.places;
};

export type FetchAutocompleteRestaurantParams = {
  search: string;
  limit?: number;
  lat?: number;
  lng?: number;
  lowCache?: boolean;
};

export const fetchAutocompleteRestaurantRequest = async ({
  search,
  limit,
  lat,
  lng,
  lowCache
}: FetchAutocompleteRestaurantParams): Promise<SimpleRestaurantResponse[]> => {
  const params = {
    search,
    limit,
    location: lat && lng ? { lat, lng } : undefined,
    lowCache
  };
  const { data } = await axios.get<SimpleRestaurantsResponse>(`/restaurant/autocomplete`, { params });
  return data.restaurants;
};

export const fetchRestaurantsRequest = async (): Promise<SimpleRestaurantResponse[]> => {
  const { data } = await axios.get<SimpleRestaurantsResponse>('/protected/restaurants');
  setCookie('restaurants', JSON.stringify(data.restaurants));
  return data.restaurants;
};

export const fetchRestaurantRequest = async (id: string, withMenu: boolean): Promise<RestaurantResponse> => {
  const { data } = await axios.get<Restaurant>(`/protected/restaurant/${id}`, { params: { withMenu } });
  return data.restaurant;
};

export const fetchPublicRestaurantRequest = async (id: string, withMenu: boolean): Promise<RestaurantResponse> => {
  const { data } = await axios.get<Restaurant>(`/restaurant/${id}`, { params: { withMenu } });
  return data.restaurant;
};

export const addRestaurantRequest = async (params: AddRestaurantParams): Promise<AddRestaurantResponse> => {
  const formData = {
    gmapPlaceId: params.placeId || null,
    gmapPlaceName: params.autocompleteName || null,
    phoneNumberOwner: params.phoneNumberOwner || null,
    firstName: params.firstName || null,
    lastName: params.lastName || null,
    customDeliveryLink: params.customDeliveryLink || null,
    uberEatsLink: params.uberEatsLink || null,
    deliverooLink: params.deliverooLink || null,
    justEatLink: params.justEatLink || null,
    alcohol: params.alcohol !== undefined ? params.alcohol : null,
    tagIds: params.tagIds || [],
    suppliers: params.suppliers || [],
    company: params.company || null
  };

  const { data } = await axios.put(`/protected/restaurant/${params.id}/proposal`, formData, {
    headers: {
      'Content-Type': 'application/json'
    }
  });

  return data;
};

export const addRestaurantFromBORequest = async (params: AddRestaurantParamsFromBO): Promise<AddRestaurantResponse> => {
  const formData = {
    gmapPlaceId: params.placeId || null,
    gmapPlaceName: params.autocompleteName || null,
    phone: params.phone || null,
    firstName: params.firstName || null,
    lastName: params.lastName || null,
    email: params.email || null,
    customDeliveryLink: params.customDeliveryLink || null,
    uberEatsLink: params.uberEatsLink || null,
    deliverooLink: params.deliverooLink || null,
    justEatLink: params.justEatLink || null,
    alcohol: params.alcohol !== undefined ? params.alcohol : null,
    tags: params.tags || [],
    suppliers: params.suppliers || [],
    company: params.company || null
  };

  const { data } = await axios.post('/proposal/ask/owner', formData, {
    headers: {
      'Content-Type': 'application/json'
    }
  });

  return data;
};

export const checkRestaurantFromBOTokenRequest = async (token: string): Promise<RestaurantCheckTokenResponse> => {
  const { data } = await axios.post(`/restaurant/${token}/check/owner`);
  return data;
};

export const deleteRestaurantRequest = async (restaurantId: string): Promise<DeleteRestaurantResponse> => {
  const { data } = await axios.delete<DeleteRestaurantResponse>(`/protected/restaurant/${restaurantId}`);
  return data;
};

export const fetchRestaurantsByOwnerRequest = async (params?: FetchRestaurantsByOwnerParams): Promise<SimpleRestaurantResponse[]> => {
  const search = params?.search === '' ? undefined : params?.search;
  const { data } = await axios.get<SimpleRestaurantsResponse>('/protected/restaurants/owner', {
    params: { search }
  });
  return data.restaurants;
};

export const fetchRestaurantPictureById = async ({ restaurantId, pictureId }: RestaurantPictureParams): Promise<GetRestaurantPictureResponse> => {
  const { data } = await axios.get<GetRestaurantPictureResponse>(`/protected/restaurant/${restaurantId}/picture/${pictureId}`);
  return data;
};

export const fetchRestaurantPictures = async (restaurantId: string): Promise<ISerializedPicture[]> => {
  const { data } = await axios.get<GetRestaurantPicturesResponse>(`/protected/restaurant/${restaurantId}/picture/`);
  return data.pictures;
};

export const deleteRestaurantPictureById = async ({ restaurantId, pictureId }: RestaurantPictureParams): Promise<UpdateRestaurantResponse> => {
  const { data } = await axios.delete<UpdateRestaurantResponse>(`/protected/restaurant/${restaurantId}/picture/${pictureId}`);
  return data;
};

export const updateRestaurantPictureById = async ({ restaurantId, pictureId, principal }: UpdateRestaurantPictureParams): Promise<UpdateRestaurantResponse> => {
  const { data } = await axios.put<UpdateRestaurantResponse>(`/protected/restaurant/${restaurantId}/picture/${pictureId}`, { principal });
  return data;
};

export const addRestaurantPicture = async ({ restaurantId, file }: AddRestaurantPictureParams): Promise<GetRestaurantPicturesResponse> => {
  const formData = new FormData();
  formData.append('picture', file);

  const { data } = await axios.put<GetRestaurantPicturesResponse>(`/protected/restaurant/${restaurantId}/picture/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return data;
};

export const approveInactiveRestaurantRequest = async (restaurantId: string): Promise<UpdateRestaurantResponse> => {
  const { data } = await axios.patch<UpdateRestaurantResponse>(`/protected/restaurant/${restaurantId}/approve`);
  return data;
};

export const rejectInactiveRestaurantRequest = async (restaurantId: string, comment: string): Promise<UpdateRestaurantResponse> => {
  const { data } = await axios.patch<UpdateRestaurantResponse>(`/protected/restaurant/${restaurantId}/reject`, {
    comment
  });
  return data;
};

export const fetchAvailableNearbyCitiesRequest = async (restaurantId: string): Promise<CityStripeStatus[]> => {
  const { data } = await axios.get<CityStripeStatus[]>(`/protected/restaurant/${restaurantId}/nearby-cities`);
  return data;
};
